import { Capacitor } from '@capacitor/core'

// iOS 键盘收起后偶尔不恢复视口（App 底部留白带 / 底栏悬空 / 输入栏被裁）。
// 这里把「可用高度」写进 CSS 变量，外壳和它实时对齐。
//
// 真机实测（iPhone 16 Pro，物理高 874）：WKWebView 可视区只有 778→840
// （底部 34px 手势条被系统占掉）→ 外壳高度取 min(屏高, 可视视口)：
// 取大了会把底栏推出屏幕，取小了底部留白。
// 只有「明显变矮 + 有输入框聚焦」才是真键盘，这时才收缩到键盘上方。
export const VIEWPORT_HEIGHT_VAR = '--app-viewport-height'
export const COMPACT_HEIGHT = 640

function isTypingTarget(el: Element | null): boolean {
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || (el as HTMLElement).isContentEditable === true
}

// 键盘收起瞬间 inner/visual 可能双双停在矮值（原 bug）：记住上一个正常高度先顶着，
// 等真实 resize 到达再纠正，避免「底栏悬空」的闪动。
let lastGood = 0
let lastNativeWindowHeight = 0

export function syncViewportHeight(target: Document = document): number | null {
  const viewport = typeof window !== 'undefined' ? window.visualViewport : undefined
  const inner = typeof window !== 'undefined' ? window.innerHeight : 0
  const visual = viewport?.height ?? inner
  const screenHeight = typeof window !== 'undefined' ? (window.screen?.height ?? 0) : 0
  const raw = Math.max(inner || 0, visual || 0)
  if (!raw) return null
  const native = Capacitor.isNativePlatform()
  // iPad multitasking and rotation change the app window without changing the
  // physical display. UIKit publishes UIWindow bounds, never a keyboard-resized
  // WebView frame, so resizing the window cannot be mistaken for a keyboard.
  const nativeHeight = native && typeof window !== 'undefined'
    ? Number((window as Window & { __shixuWindowHeight?: number }).__shixuWindowHeight) || 0
    : 0
  const windowHeight = nativeHeight > 0 ? nativeHeight : screenHeight
  if (nativeHeight > 0 && nativeHeight !== lastNativeWindowHeight) lastGood = 0
  lastNativeWindowHeight = nativeHeight
  // 键盘判定要用「可视视口」而不是两者最大值：铺满全屏后 innerHeight 不会跟着键盘缩，
  // 只看 max 会漏掉键盘（登录页按钮又会被盖住）。
  const short = windowHeight > 0 && visual < windowHeight - 120
  const keyboardOpen = short && isTypingTarget(target.activeElement)
  let height = raw
  if (short) {
    // 明显矮：真键盘就收缩到键盘上方；不是键盘（收起瞬间的残留值）先用上一个正常高度顶着
    height = keyboardOpen ? Math.min(inner || visual, visual) : lastGood || raw
  } else if (native && windowHeight > 0) {
    // 正常状态：不超出物理屏，也不超出 WebView 可视区
    height = Math.min(windowHeight, raw)
  }
  const rounded = Math.round(height)
  // 正常高度不能被键盘高度覆盖，否则收起键盘的残留矮值会永久成为恢复目标。
  if (native && !short) lastGood = rounded
  if (native) {
    // 原生壳标记：CSS 用它去掉导航/输入栏多余的底部安全区（外壳已避开手势条）
    target.documentElement.setAttribute('data-native', '1')
    // 键盘弹出中标记：键盘就是真正的底部边界，CSS 用它去掉 safe-area 占位，
    // 否则输入框和键盘之间会空出一条 34pt 的白色缝隙（用户报「中间隔了一段」）。
    if (keyboardOpen) target.documentElement.setAttribute('data-keyboard', '1')
    else target.documentElement.removeAttribute('data-keyboard')
    // 真机排查用：直接进设备日志（devicectl --console）就能看到外壳高度是否正确
    console.info(`[viewport] screen=${screenHeight} window=${windowHeight} inner=${inner} visual=${Math.round(visual)} keyboard=${keyboardOpen} shell=${rounded}`)
  }
  target.documentElement.style.setProperty(VIEWPORT_HEIGHT_VAR, `${rounded}px`)
  if (rounded < COMPACT_HEIGHT) target.documentElement.setAttribute('data-compact', '1')
  else target.documentElement.removeAttribute('data-compact')
  // 键盘收起后 iOS 偶尔残留页面滚动偏移（offsetTop > 0）→ 顶栏/输入栏被裁。发现就立刻复位。
  if (viewport && viewport.offsetTop > 0) {
    if (typeof window !== 'undefined') window.scrollTo(0, 0)
    if (target.documentElement.scrollTop > 0) target.documentElement.scrollTop = 0
    if (target.body.scrollTop > 0) target.body.scrollTop = 0
  }
  return rounded
}

/** 订阅可视视口变化（键盘弹出/收起、旋转、地址栏收起都会触发）。返回取消订阅函数。 */
export function watchViewportHeight(target: Document = document): () => void {
  if (typeof window === 'undefined') return () => {}
  syncViewportHeight(target)
  const viewport = window.visualViewport
  const onChange = () => { syncViewportHeight(target) }
  viewport?.addEventListener('resize', onChange)
  viewport?.addEventListener('scroll', onChange)
  window.addEventListener('orientationchange', onChange)
  window.addEventListener('resize', onChange)
  window.addEventListener('shixu-window-resize', onChange)
  target.addEventListener('focusin', onChange)
  target.addEventListener('focusout', onChange)
  return () => {
    viewport?.removeEventListener('resize', onChange)
    viewport?.removeEventListener('scroll', onChange)
    window.removeEventListener('orientationchange', onChange)
    window.removeEventListener('resize', onChange)
    window.removeEventListener('shixu-window-resize', onChange)
    target.removeEventListener('focusin', onChange)
    target.removeEventListener('focusout', onChange)
  }
}
